import type { DataItemId } from '@pn/core/domain/data';
import type {
  ProductionChartType,
  ProductionItem,
} from '@pn/core/domain/production';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isNil, uniq } from 'lodash-es';

type State = {
  isFetching: boolean;
  isFetchingExtra: boolean;
  isError: boolean;
  productionItems: ProductionItem[];
  idsLoaded: DataItemId[];
  loadedKey: string;
};

const initialState: State = {
  isFetching: false,
  isFetchingExtra: false,
  isError: false,
  productionItems: [],
  idsLoaded: [],
  loadedKey: '',
};

export const createProductionSlice = (
  productionChartType: ProductionChartType
) =>
  createSlice({
    name: `${productionChartType}-production`,
    initialState,
    reducers: {
      request: (state, { payload: key }: PayloadAction<string | undefined>) => {
        state.isFetching = true;
        if (!isNil(key)) state.loadedKey = key;
      },
      requestExtra: (state, { payload }: PayloadAction<DataItemId[]>) => {
        state.isFetchingExtra = true;
        state.idsLoaded = uniq([...state.idsLoaded, ...payload]);
      },
      error: (state) => {
        state.isFetching = false;
        state.isFetchingExtra = false;
        state.isError = true;
        state.productionItems = [];
        state.idsLoaded = [];
        // Do not reset loadedKey here as that causes an infinite reloading loop
      },
      receive: (state, { payload }: PayloadAction<ProductionItem[]>) => {
        state.isFetching = false;
        state.isError = false;
        state.productionItems = payload;
        state.idsLoaded = getUniqueIds(payload);
      },
      receiveExtra: (state, { payload }: PayloadAction<ProductionItem[]>) => {
        state.isFetchingExtra = false;
        state.productionItems = [...state.productionItems, ...payload];
      },
      removeExtra: (state, { payload }: PayloadAction<DataItemId[]>) => {
        state.idsLoaded = state.idsLoaded.filter((id) => !payload.includes(id));
        state.productionItems = state.productionItems.filter(
          (item) => !payload.includes(item.id ?? '')
        );
      },
    },
  });

function getUniqueIds(productionItems: ProductionItem[]): DataItemId[] {
  const uniqueIds = new Set<DataItemId>();

  productionItems.forEach((productionItem) => {
    if (!isNil(productionItem.id)) uniqueIds.add(productionItem.id);
  });

  return Array.from(uniqueIds);
}
